import { firstValueFrom } from 'rxjs';
import isEmpty from 'lodash/isEmpty';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IInvoiceInfo, IUserCompany, ngHelper } from '@cakeiteasy/web-utils';
import { PaymentApiService } from '../../../api-services/services/payment-api.service';
import { IUser } from '../../../users';
import { IPaymentInfo } from '../../models/i-payment-models';
import { INVOICE_SEND_FORMATS, PAYMENT_PERSON_TYPES, PAYMENT_PLATFORM_TYPES } from '../../payment.constants';
import { PaymentStorageService } from '../../services/payment-storage.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'invoice-payment-b',
  templateUrl: './invoice-payment-b.component.html',
  styleUrls: ['./invoice-payment-b.component.scss'],
})
export class InvoicePaymentBComponent implements OnChanges {
  @Input() countryCode: string;
  @Input() user: IUser;
  @Input() isSharedBusinessAccount: boolean;
  @Input() giftCardCode: string = null;
  @Input() showHeader = true;
  @Input() showInvoiceFormat = true;
  @Input() bakeryId: number = null;
  @Input() companyChangeActions: (company: IUserCompany) => Promise<any> = null;

  @Output() onSuccess = new EventEmitter<{ sid: string; paymentUrl?: string }>();
  @Output() onFail = new EventEmitter<any>();
  @Output() onInvoiceInfoUpdated = new EventEmitter<IInvoiceInfo>();
  @Output() onDiscountUpdated = new EventEmitter<{ value: number } | null>();
  @Output() onTermsClick = new EventEmitter<void>();
  @Output() onBusy = new EventEmitter<Promise<any>>();

  public invoiceInfo: IInvoiceInfo;
  public isInvoiceInfoValid = false;
  public isContactInfoFilled: boolean;

  public privatePaymentType: string = PAYMENT_PERSON_TYPES.COMPANY;
  public contactInfo: IPaymentInfo;
  public allowToChangeCountry = true;
  public errorMsg = '';

  constructor(
    protected _paymentApi: PaymentApiService,
    protected _paymentStorage: PaymentStorageService,
    protected _translate: TranslateService,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    const { invoice } = PAYMENT_PLATFORM_TYPES;

    ngHelper.ifChanged(changes, 'user', (user: IUser, prevUser: IUser) => {
      let invoiceInfo = this._paymentStorage.getInvoiceInfo();

      if (user) {
        if (!this.isSharedBusinessAccount && !prevUser) {
          this._paymentStorage.updateContactInfo(invoice, null, user);
        }

        if (user.invoiceInfo) {
          const { company } = user.invoiceInfo;

          if (this.isSharedBusinessAccount) {
            invoiceInfo = this._makeSharedBusinessInvoiceInfo(user, invoiceInfo?.reference);
          } else if (!invoiceInfo?.company) {
            invoiceInfo = user.invoiceInfo;
          }

          if (company?.discount) {
            this.onDiscountUpdated.emit({
              value: company.discount,
            });
          }
        }
      }

      this._paymentStorage.setInvoiceInfo(invoiceInfo);
      this.invoiceInfo = isEmpty(invoiceInfo) ? null : { ...invoiceInfo };
      this.contactInfo = this._paymentStorage.getContactInfo(invoice);
    });
  }

  public onUpdated(info: IInvoiceInfo) {
    const invoiceInfo = {
      ...this.invoiceInfo,
      ...info,
      contactPersonName: this.contactInfo && this.contactInfo.userName,
      email: info.email || (this.contactInfo && this.contactInfo.userEmail),
    };

    this._paymentStorage.setInvoiceInfo(invoiceInfo);
    this.invoiceInfo = { ...invoiceInfo };

    if (info && info.company) {
      this.onDiscountUpdated.emit({
        value: info.company.discount || 0,
      });
    }

    if (!this.isSharedBusinessAccount) {
      this.onInvoiceInfoUpdated.emit(info);
    }
  }

  public async createInvoiceOrder(invoiceInfo: IInvoiceInfo, paymentContactInfo: IPaymentInfo) {
    try {
      paymentContactInfo = {
        ...paymentContactInfo,
        invoiceEmail: invoiceInfo.email || paymentContactInfo.invoiceEmail || paymentContactInfo.userEmail,
      };

      const res$ = this.createInvoice(invoiceInfo, paymentContactInfo);
      this.onBusy.emit(res$);
      this.onSuccess.emit(await res$);
    } catch (err) {
      this.errorMsg = this.getErrorMessage(err);
      this.onFail.emit(err);
    }
  }

  public onContactInfoValidated(isValid: boolean) {
    setTimeout(() => {
      this.isContactInfoFilled = isValid;
    });
  }

  public onContactInfoUpdated(paymentInfo: IPaymentInfo) {
    const { invoice } = PAYMENT_PLATFORM_TYPES;
    this.contactInfo = { ...this.contactInfo, ...paymentInfo };
    this._paymentStorage.updateContactInfo(invoice, this.contactInfo);
  }

  public async onCompanySelected(company: IUserCompany) {
    this.invoiceInfo = this._makeInvoiceInfo(company);
    this._paymentStorage.setInvoiceInfo(this.invoiceInfo);

    if (this.companyChangeActions) {
      await this.companyChangeActions(company);
    }
  }

  public onInvoiceCompanySelected(company: IUserCompany) {
    this.invoiceInfo = this._makeInvoiceInfo(company);
    this._paymentStorage.setInvoiceInfo(this.invoiceInfo);

    this.onContactInfoUpdated({
      userCompanyName: company.legalCompany.name,
      invoiceCompanyId: company.id,
      invoiceCompanyOrgNumber: company.legalCompany.orgNumber,
      invoiceCompanyDiscount: company.discount,
      invoiceCompanyCountry: company.legalCompany.country,
    });
  }

  public createInvoice(invoiceInfo: IInvoiceInfo, paymentContactInfo: IPaymentInfo): Promise<{ sid: string }> {
    paymentContactInfo = { ...paymentContactInfo, code: this.giftCardCode };
    return firstValueFrom(this._paymentApi.createInvoice(invoiceInfo, paymentContactInfo));
  }

  public getErrorMessage(response: any): string {
    return '';
  }

  private _makeSharedBusinessInvoiceInfo(user: IUser, reference: string) {
    return {
      format: user.invoiceInfo.format,
      email: user.invoiceInfo.email,
      company: { ...(user.invoiceInfo.company || this.invoiceInfo.company) },
      contactPersonName: this.contactInfo && this.contactInfo.userName,
      orgName: user.invoiceInfo.orgName,
      orgNumber: user.invoiceInfo.orgNumber,
      reference,
    };
  }

  private _makeInvoiceInfo(company: IUserCompany): IInvoiceInfo {
    return {
      ...this.invoiceInfo,
      company,
      type: 'COMPANY',
      format: this.invoiceInfo?.format || INVOICE_SEND_FORMATS.EHF,
      orgNumber: company.legalCompany.orgNumber,
      orgName: company.legalCompany.name,
      contactPersonName: this.contactInfo?.userName || '',
      email: this.invoiceInfo?.email || this.contactInfo?.userEmail || '',
    } as IInvoiceInfo;
  }
}
